import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  StressScenario,
  ScenarioListItem,
  ElementOverride,
  CreateScenarioRequest,
  UpdateScenarioRequest,
  ScenarioParameters,
  PlanningMeasure
} from '../types/scenario';
import { Hausanschluss, TechnischeRessource } from '../types/network';
import { authService } from '../services/authService';
import { useZoneStore } from './zoneStore';

interface ScenarioState {
  // Aktives Szenario
  currentScenario: StressScenario | null;

  // Lokale Änderungen (noch nicht gespeichert)
  pendingOverrides: Record<string, ElementOverride>;
  pendingAddedElements: Hausanschluss[];
  /** Grid reinforcement measures (cable type / transformer rating), unsaved until saveScenario. */
  pendingMeasures: PlanningMeasure[];

  // UI-State
  isDirty: boolean;
  isSaving: boolean;
  isLoading: boolean;
  error: string | null;

  // Ausgewähltes Element für Bearbeitung
  selectedElementId: string | null;

  // Liste aller gespeicherten Szenarien
  scenarioList: ScenarioListItem[];
  isLoadingList: boolean;
}

interface ScenarioActions {
  // CRUD
  loadScenarioList: (zoneId?: string) => Promise<void>;
  loadScenario: (id: string, zoneId?: string) => Promise<void>;
  createScenario: (request: CreateScenarioRequest, zoneId?: string) => Promise<StressScenario | null>;
  saveScenario: (paramsOverride?: ScenarioParameters, zoneId?: string) => Promise<boolean>;
  deleteScenario: (id: string, zoneId?: string) => Promise<boolean>;
  duplicateScenario: (id: string, newName: string) => Promise<StressScenario | null>;

  // Bearbeitung
  updateElementOverride: (elementId: string, override: Partial<ElementOverride>) => void;
  removeElementOverride: (elementId: string) => void;
  addTechnischeRessource: (elementId: string, resource: TechnischeRessource) => void;
  removeTechnischeRessource: (elementId: string, resourceId: string) => void;
  addElement: (element: Hausanschluss) => void;
  removeAddedElement: (elementId: string) => void;
  setMeasure: (measure: PlanningMeasure) => void;
  removeMeasure: (measureId: string) => void;
  hasMeasure: (elementId: string) => boolean;

  // UI
  setSelectedElementId: (elementId: string | null) => void;
  resetPendingChanges: () => void;
  clearCurrentScenario: () => void;

  // Helpers
  getElementOverride: (elementId: string) => ElementOverride | undefined;
  hasOverride: (elementId: string) => boolean;
  getTotalChangesCount: () => number;
}

type ScenarioStore = ScenarioState & ScenarioActions;

const getBackendUrl = (): string => {
  // Use relative URLs - nginx proxy will route to backend
  return '';
};

const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = authService.getToken();
  const tenantId = authService.getTenantId();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

/**
 * Get the scenario API base URL.
 * If a zoneId override is provided, uses that zone.
 * Otherwise falls back to the active zone from zoneStore.
 * If no zone is active, uses the global endpoint.
 */
const getScenarioApiUrl = (zoneIdOverride?: string): string => {
  const zoneId = zoneIdOverride ?? useZoneStore.getState().activeZoneId;
  if (zoneId) {
    return `${getBackendUrl()}/api/zones/${zoneId}/Scenario`;
  }
  return `${getBackendUrl()}/api/Scenario`;
};

export const useScenarioStore = create<ScenarioStore>()(
  devtools((set, get) => ({
    // Initialer State
    currentScenario: null,
    pendingOverrides: {},
    pendingAddedElements: [],
    pendingMeasures: [],
    isDirty: false,
    isSaving: false,
    isLoading: false,
    error: null,
    selectedElementId: null,
    scenarioList: [],
    isLoadingList: false,

    // === CRUD Actions ===

    loadScenarioList: async (zoneId?: string) => {
      set({ isLoadingList: true, error: null }, false, 'loadScenarioList/start');
      try {
        const response = await fetch(getScenarioApiUrl(zoneId), {
          headers: getAuthHeaders(),
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error(`Fehler beim Laden der Szenarien: ${response.status}`);
        }
        const scenarios: ScenarioListItem[] = await response.json();
        set({ scenarioList: scenarios, isLoadingList: false }, false, 'loadScenarioList/success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ error: message, isLoadingList: false }, false, 'loadScenarioList/error');
      }
    },

    loadScenario: async (id: string, zoneId?: string) => {
      set({ isLoading: true, error: null }, false, 'loadScenario/start');
      try {
        const response = await fetch(`${getScenarioApiUrl(zoneId)}/${id}`, {
          headers: getAuthHeaders(),
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error(`Fehler beim Laden des Szenarios: ${response.status}`);
        }
        const scenario: StressScenario = await response.json();
        set({
          currentScenario: scenario,
          pendingOverrides: { ...scenario.elementOverrides },
          pendingAddedElements: [...scenario.addedElements],
          pendingMeasures: [...(scenario.measures ?? [])],
          isDirty: false,
          isLoading: false
        }, false, 'loadScenario/success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ error: message, isLoading: false }, false, 'loadScenario/error');
      }
    },

    createScenario: async (request: CreateScenarioRequest, zoneId?: string) => {
      // Behalte aktuelle Änderungen, um sie ins neue Szenario zu übernehmen
      const { pendingOverrides, pendingAddedElements } = get();
      const hasChanges = Object.keys(pendingOverrides).length > 0 || pendingAddedElements.length > 0;

      set({ isSaving: true, error: null }, false, 'createScenario/start');
      try {
        const response = await fetch(getScenarioApiUrl(zoneId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(request)
        });
        if (!response.ok) {
          throw new Error(`Fehler beim Erstellen des Szenarios: ${response.status}`);
        }
        const scenario: StressScenario = await response.json();

        // Behalte pendingOverrides wenn vorhanden, damit sie gespeichert werden können
        set({
          currentScenario: scenario,
          pendingOverrides: pendingOverrides,
          pendingAddedElements: pendingAddedElements,
          isDirty: hasChanges,
          isSaving: false
        }, false, 'createScenario/success');

        // Wenn es Änderungen gab, direkt speichern
        if (hasChanges) {
          await get().saveScenario(undefined, zoneId);
        }

        // Liste aktualisieren
        await get().loadScenarioList(zoneId);
        return scenario;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ error: message, isSaving: false }, false, 'createScenario/error');
        return null;
      }
    },

    saveScenario: async (paramsOverride?: ScenarioParameters, zoneId?: string) => {
      const { currentScenario, pendingOverrides, pendingAddedElements, pendingMeasures } = get();
      if (!currentScenario) {
        set({ error: 'Kein Szenario ausgewählt' }, false, 'saveScenario/noScenario');
        return false;
      }

      set({ isSaving: true, error: null }, false, 'saveScenario/start');
      try {
        const updateRequest: UpdateScenarioRequest = {
          elementOverrides: pendingOverrides,
          addedElements: pendingAddedElements,
          measures: pendingMeasures,
          parameters: paramsOverride
        };

        const response = await fetch(`${getScenarioApiUrl(zoneId)}/${currentScenario.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(updateRequest)
        });

        if (!response.ok) {
          throw new Error(`Fehler beim Speichern des Szenarios: ${response.status}`);
        }

        const updatedScenario: StressScenario = await response.json();
        set({
          currentScenario: updatedScenario,
          isDirty: false,
          isSaving: false
        }, false, 'saveScenario/success');

        // Liste aktualisieren
        await get().loadScenarioList(zoneId);
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ error: message, isSaving: false }, false, 'saveScenario/error');
        return false;
      }
    },

    deleteScenario: async (id: string, zoneId?: string) => {
      try {
        const response = await fetch(`${getScenarioApiUrl(zoneId)}/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error(`Fehler beim Löschen des Szenarios: ${response.status}`);
        }

        // Wenn das gelöschte Szenario das aktuelle war, zurücksetzen
        const { currentScenario } = get();
        if (currentScenario?.id === id) {
          set({
            currentScenario: null,
            pendingOverrides: {},
            pendingAddedElements: [],
            pendingMeasures: [],
            isDirty: false
          }, false, 'deleteScenario/clearCurrent');
        }

        // Liste aktualisieren
        await get().loadScenarioList(zoneId);
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ error: message }, false, 'deleteScenario/error');
        return false;
      }
    },

    duplicateScenario: async (id: string, newName: string) => {
      try {
        const response = await fetch(`${getScenarioApiUrl()}/${id}/duplicate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ newName })
        });

        if (!response.ok) {
          throw new Error(`Fehler beim Duplizieren des Szenarios: ${response.status}`);
        }

        const scenario: StressScenario = await response.json();
        await get().loadScenarioList();
        return scenario;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ error: message }, false, 'duplicateScenario/error');
        return null;
      }
    },

    // === Bearbeitungs-Actions ===

    updateElementOverride: (elementId: string, override: Partial<ElementOverride>) => {
      set(state => {
        const existing = state.pendingOverrides[elementId] || {};
        const updated = { ...existing, ...override };

        // Entferne leere/undefined Werte
        const cleaned: ElementOverride = {};
        if (updated.jahresverbrauch !== undefined) cleaned.jahresverbrauch = updated.jahresverbrauch;
        if (updated.connectionPower !== undefined) cleaned.connectionPower = updated.connectionPower;
        if (updated.loadProfile !== undefined) cleaned.loadProfile = updated.loadProfile;
        if (updated.baseLoadKw !== undefined) cleaned.baseLoadKw = updated.baseLoadKw;
        if (updated.addedResources && updated.addedResources.length > 0) cleaned.addedResources = updated.addedResources;
        if (updated.removedResourceIds && updated.removedResourceIds.length > 0) cleaned.removedResourceIds = updated.removedResourceIds;
        if (updated.modifiedResources && Object.keys(updated.modifiedResources).length > 0) cleaned.modifiedResources = updated.modifiedResources;

        return {
          pendingOverrides: {
            ...state.pendingOverrides,
            [elementId]: cleaned
          },
          isDirty: true
        };
      }, false, 'updateElementOverride');
    },

    removeElementOverride: (elementId: string) => {
      set(state => {
        const { [elementId]: _removed, ...rest } = state.pendingOverrides;
        void _removed; // Intentionally unused - destructuring to remove key
        return {
          pendingOverrides: rest,
          isDirty: true
        };
      }, false, 'removeElementOverride');
    },

    addTechnischeRessource: (elementId: string, resource: TechnischeRessource) => {
      set(state => {
        const existing = state.pendingOverrides[elementId] || {};
        const addedResources = [...(existing.addedResources || []), resource];
        return {
          pendingOverrides: {
            ...state.pendingOverrides,
            [elementId]: { ...existing, addedResources }
          },
          isDirty: true
        };
      }, false, 'addTechnischeRessource');
    },

    removeTechnischeRessource: (elementId: string, resourceId: string) => {
      set(state => {
        const existing = state.pendingOverrides[elementId] || {};

        // Prüfe ob es eine hinzugefügte Ressource ist
        if (existing.addedResources?.some(r => r.id === resourceId)) {
          const addedResources = existing.addedResources.filter(r => r.id !== resourceId);
          return {
            pendingOverrides: {
              ...state.pendingOverrides,
              [elementId]: { ...existing, addedResources }
            },
            isDirty: true
          };
        }

        // Sonst zu removedResourceIds hinzufügen
        const removedResourceIds = [...(existing.removedResourceIds || []), resourceId];
        return {
          pendingOverrides: {
            ...state.pendingOverrides,
            [elementId]: { ...existing, removedResourceIds }
          },
          isDirty: true
        };
      }, false, 'removeTechnischeRessource');
    },

    addElement: (element: Hausanschluss) => {
      set(state => ({
        pendingAddedElements: [...state.pendingAddedElements, element],
        isDirty: true
      }), false, 'addElement');
    },

    removeAddedElement: (elementId: string) => {
      set(state => ({
        pendingAddedElements: state.pendingAddedElements.filter(e => e.id !== elementId),
        isDirty: true
      }), false, 'removeAddedElement');
    },

    setMeasure: (measure: PlanningMeasure) => {
      set(state => ({
        pendingMeasures: [
          ...state.pendingMeasures.filter(m => m.id !== measure.id && !(m.kind === measure.kind && m.elementId === measure.elementId)),
          measure,
        ],
        isDirty: true
      }), false, 'setMeasure');
    },

    removeMeasure: (measureId: string) => {
      set(state => ({
        pendingMeasures: state.pendingMeasures.filter(m => m.id !== measureId),
        isDirty: true
      }), false, 'removeMeasure');
    },

    hasMeasure: (elementId: string): boolean => get().pendingMeasures.some(m => m.elementId === elementId),

    // === UI Actions ===

    setSelectedElementId: (elementId: string | null) => {
      set({ selectedElementId: elementId }, false, 'setSelectedElementId');
    },

    resetPendingChanges: () => {
      const { currentScenario } = get();
      set({
        pendingOverrides: currentScenario ? { ...currentScenario.elementOverrides } : {},
        pendingAddedElements: currentScenario ? [...currentScenario.addedElements] : [],
        pendingMeasures: currentScenario ? [...(currentScenario.measures ?? [])] : [],
        isDirty: false
      }, false, 'resetPendingChanges');
    },

    clearCurrentScenario: () => {
      set({
        currentScenario: null,
        pendingOverrides: {},
        pendingAddedElements: [],
        pendingMeasures: [],
        isDirty: false,
        selectedElementId: null,
        error: null
      }, false, 'clearCurrentScenario');
    },

    // === Helpers ===

    getElementOverride: (elementId: string) => {
      return get().pendingOverrides[elementId];
    },

    hasOverride: (elementId: string): boolean => {
      const override = get().pendingOverrides[elementId];
      if (!override) return false;

      return !!(
        override.jahresverbrauch !== undefined ||
        override.connectionPower !== undefined ||
        override.loadProfile !== undefined ||
        override.baseLoadKw !== undefined ||
        (override.addedResources && override.addedResources.length > 0) ||
        (override.removedResourceIds && override.removedResourceIds.length > 0) ||
        (override.modifiedResources && Object.keys(override.modifiedResources).length > 0)
      );
    },

    getTotalChangesCount: () => {
      const { pendingOverrides, pendingAddedElements, pendingMeasures } = get();
      const overrideCount = Object.values(pendingOverrides).filter(o => {
        return (
          o.jahresverbrauch !== undefined ||
          o.connectionPower !== undefined ||
          o.loadProfile !== undefined ||
          o.baseLoadKw !== undefined ||
          (o.addedResources && o.addedResources.length > 0) ||
          (o.removedResourceIds && o.removedResourceIds.length > 0) ||
          (o.modifiedResources && Object.keys(o.modifiedResources).length > 0)
        );
      }).length;
      return overrideCount + pendingAddedElements.length + pendingMeasures.length;
    }
  }))
);
